import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/system";

import { emailSignatureDefaultSelector } from "@/pages/Private/redux/copywritingSettings/copywritingSettings.slice";
import { useAppSelector } from "@/redux/hooks";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";

import { useSendTestMessageMutation } from "@/pages/Private/redux/schedule/schedule.api";
import {
	SendTestMessageRequest,
	SendTestMessageRequestSchema,
	SendTestMessageResponse,
} from "@/pages/Private/pages/Account/schema/sendTestMessage";

import { ERROR_TYPE } from "@/utils";
import { useSnackBar } from "@/utils/useSnackbar";

import { Draft } from "../utils/inboxDraft.types";
import { mapDraftToTestMessageRequest } from "../utils/draftToTestMessageRequest.util";

import { TestSendErrors } from "./TestSendErrors";
import { TestMessagePreview } from "./TestMessagePreview";

interface SendTestChatMessageModalProps {
	isOpen: boolean;
	onClose: () => void;
	draft: Draft;
	listPersonId?: number;
	defaultRecipientEmail?: string;
}

/**
 * Sends a test message for the active inbox-chat draft. Unlike the node-based
 * `SendTestMessageModal`, the sender / channel / subject / body are locked to the
 * draft the user is composing — only the test recipient is collected here. Reuses
 * the existing `POST schedule/send-test-message` endpoint (no backend changes).
 */
export const SendTestChatMessageModal: FunctionComponent<SendTestChatMessageModalProps> = ({
	isOpen,
	onClose,
	draft,
	listPersonId,
	defaultRecipientEmail,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`campaign.sendTest.${key}`);

	const [sendTestMessage, { isLoading, error, reset: resetMutation }] =
		useSendTestMessageMutation();
	const [preview, setPreview] = useState<SendTestMessageResponse | null>(null);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const channel = draft.from?.type === "email" ? "email" : "linkedin";
	const signatureDefault = useAppSelector(emailSignatureDefaultSelector);

	const defaultValues = useMemo<SendTestMessageRequest>(
		() =>
			mapDraftToTestMessageRequest(
				draft,
				listPersonId ?? 0,
				channel === "email" ? (defaultRecipientEmail ?? "") : "",
				signatureDefault
			),
		[draft, listPersonId, channel, defaultRecipientEmail, signatureDefault]
	);

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm<SendTestMessageRequest>({
		resolver: zodResolver(SendTestMessageRequestSchema),
		defaultValues: defaultValues as SendTestMessageRequest,
	});

	const formErrorsByName = errors as Record<string, { message?: string } | undefined>;

	const defaultValuesRef = useRef(defaultValues);

	defaultValuesRef.current = defaultValues;

	useEffect(() => {
		if (isOpen) {
			reset(defaultValuesRef.current as SendTestMessageRequest);
			setPreview(null);
			resetMutation();
		}
	}, [isOpen]);

	const recipientEmail = channel === "email" ? watch("recipientEmail") : "";
	const recipientLinkedinUrl = channel === "linkedin" ? watch("recipientLinkedinUrl") : "";

	const onSubmit = async (values: SendTestMessageRequest) => {
		try {
			const result = await sendTestMessage(values).unwrap();

			setPreview(result);
			handleOpenSnackbar(t("campaign.sendTest.successToast", { sentTo: result.sentTo }) as string);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors)
		.map((e) => (e as { message?: string })?.message)
		.filter(Boolean) as ERROR_TYPE[];

	const canSubmit = !!listPersonId;

	return (
		<Modal
			disableSave={!canSubmit}
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			size="sm"
			submitButtonText={ts("sendButton")}
			title={ts("title")}
		>
			{renderMessageSnackbar()}
			<Stack spacing={2}>
				{channel === "email" ? (
					<InputField
						error={!!formErrorsByName.recipientEmail?.message}
						handleChange={(e) =>
							setValue("recipientEmail", e.target.value, { shouldValidate: true })
						}
						label={ts("recipientEmail")}
						name="recipientEmail"
						placeholder={ts("recipientEmailPlaceholder")}
						type="email"
						value={recipientEmail ?? ""}
					/>
				) : (
					<InputField
						error={!!formErrorsByName.recipientLinkedinUrl?.message}
						handleChange={(e) =>
							setValue("recipientLinkedinUrl", e.target.value, { shouldValidate: true })
						}
						label={ts("recipientLinkedinUrl")}
						name="recipientLinkedinUrl"
						placeholder={ts("recipientLinkedinUrlPlaceholder")}
						value={recipientLinkedinUrl ?? ""}
					/>
				)}

				{preview && <TestMessagePreview preview={preview} />}

				<TestSendErrors error={error} formErrors={formErrors} />
			</Stack>
		</Modal>
	);
};
